import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, Drawer, IconButton, Snackbar, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useGlobalState } from '../../../store/GlobalStore';
import { usePost } from '../../../utils/apiHelper';
import { IExternalAccountSyncBulkActionResult } from '../../../models/ExternalAccountSync/IExternalAccountSync';

interface ApproveRecordDrawerProps {
    open: boolean;
    stagingIds: number[];
    label?: string;
    demoMode?: boolean;
    onClose: () => void;
    onApproved: (stagingIds: number[]) => void;
}

const ApproveRecordDrawer: React.FC<ApproveRecordDrawerProps> = ({ open, stagingIds, label, demoMode, onClose, onApproved }) => {
    const { state } = useGlobalState();
    const actionByEmail = state.user?.email || state.userAccessContext?.userName || '';

    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);

    const isBulk = stagingIds.length > 1;

    const handleClose = () => {
        if (submitting) return;
        setErrorMessage('');
        onClose();
    };

    const handleConfirm = async () => {
        if (stagingIds.length === 0) return;

        setSubmitting(true);
        setErrorMessage('');

        try {
            if (demoMode) {
                await new Promise((resolve) => setTimeout(resolve, 300));
                onApproved(stagingIds);
                setSuccessOpen(true);
                onClose();
                return;
            }

            if (isBulk) {
                const response = await usePost<IExternalAccountSyncBulkActionResult>('ExternalAccountSyncBulkApprove', {
                    stagingIds,
                    actionByEmail,
                });
                onApproved(response.data?.succeededIds || []);
            } else {
                await usePost('ExternalAccountSyncApprove', { stagingId: stagingIds[0], actionByEmail });
                onApproved(stagingIds);
            }
            setSuccessOpen(true);
            onClose();
        } catch (err) {
            setErrorMessage('Failed to approve record(s). Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <Drawer open={open} anchor="right" onClose={handleClose}>
                <Box display="flex" flexDirection="column" height="100%" width={400} maxWidth="95vw" sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#007FFF', color: '#fff', padding: '14px 44px 14px 20px', flexShrink: 0 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            Approve {isBulk ? `${stagingIds.length} Records` : (label || 'Record')}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            disabled={submitting}
                            size="small"
                            sx={{ position: 'absolute', right: 6, top: 9, color: 'white' }}
                        >
                            <HighlightOffIcon style={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ padding: '18px' }}>
                        <Box display="flex" alignItems="flex-start" sx={{ gap: 1.25, backgroundColor: '#eef6ff', border: '1px solid #cfe6ff', borderRadius: 1.5, padding: '12px 14px' }}>
                            <InfoOutlinedIcon fontSize="small" sx={{ color: '#0d47a1', mt: '2px', flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#0d3c78', lineHeight: 1.5, fontSize: '0.85rem' }}>
                                {isBulk
                                    ? `${stagingIds.length} records will be marked Processed and recorded in the audit history.`
                                    : 'This record will be marked Processed and recorded in the audit history.'}
                            </Typography>
                        </Box>

                        {errorMessage && (
                            <Typography sx={{ fontSize: '0.82rem', color: '#b3261e', mt: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>

                    <Box flexGrow={1} />

                    <Box display="flex" sx={{ gap: 1.25, padding: '14px 18px 18px', borderTop: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
                        <Button size="small" variant="outlined" fullWidth disabled={submitting} onClick={handleClose} sx={{ borderColor: '#c7cbd1', color: '#5b625b', textTransform: 'none', fontWeight: 600 }}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            color="primary"
                            disableElevation
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                            disabled={submitting}
                            onClick={handleConfirm}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {submitting ? 'Approving…' : 'Confirm'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Snackbar open={successOpen} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={() => setSuccessOpen(false)}>
                <Alert onClose={() => setSuccessOpen(false)} severity="success" elevation={6} variant="filled">
                    {isBulk ? 'Records approved.' : 'Record approved.'}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
};

export default ApproveRecordDrawer;
